import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Time} from 'yougile-util'
import {Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {
  TaskType,
  TaskWithColor,
  useCalendarStore
} from '@components/calendar/calendar-store'
import {rightPanelStore} from '@components/right-panel/right-panel'
import {noPropagate} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {minTaskHeight, roundNumberTo} from '../../utils'
import {useDraggableStore} from './draggable-store'

const DraggableTw = tw.div<{
  $changeDeadlineExpansion: boolean
  $taskInPast?: boolean
}>`
  bg-main-old
  absolute
  cursor-row-resize
  h-2
  w-2/6
  right-1/3
  left-1/3
  rounded-2
  z-20
  ${p => (p.$changeDeadlineExpansion ? '-bottom-4' : 'bottom-4')}
  ${p => (p.$taskInPast ? 'opacity-65' : '')}
`

const DraggableContainerTw = tw.div<{
  $changeDeadlineExpansion: boolean
  $whiteBg?: boolean
  $taskInPast?: boolean
}>`
  absolute
  cursor-row-resize
  z-20
  ${p => (p.$taskInPast ? 'opacity-65' : '')}
    ${p =>
      p.$changeDeadlineExpansion
        ? `
          h-8
          border
          border-t-0
          border-solid
          right-0
          left-0
          -bottom-8
          rounded-bl-4
          rounded-br-4
          border-bg-main-old
          ${p.$whiteBg ? 'bg-on-color' : ''}
        `
        : `
          bottom-0
          left-0
          w-full
          h-14
        `}
`

const ActiveTaskBar = tw.div`
  absolute
  top-0
  bottom-0
  left-0
  w-4
  bg-accent-old
  rounded-bl-4
`

export const DraggableDeadline = observer(
  // eslint-disable-next-line max-lines-per-function
  React.forwardRef(
    (
      {
        task,
        type,
        dragWithoutStartDate = false,
        changeDeadlineExpansion,
        isTaskInPast
      }: {
        task: TaskWithColor
        type: TaskType
        dragWithoutStartDate?: boolean
        changeDeadlineExpansion: boolean
        isTaskInPast: boolean
      },
      ref: React.MutableRefObject<HTMLElement>
    ) => {
      const [state, store] = useCalendarStore()
      const [draggableState, draggableStore] = useDraggableStore()

      const getDeadlineTime = () => {
        let startDate: number

        if (dragWithoutStartDate) {
          startDate =
            Time.toTimestamp(state.initialEndDateOnDrag) -
            minTaskHeight * Time.minute
        } else {
          startDate = Time.toTimestamp(state.initialStartDateOnDrag)
        }

        const initialStart = startDate + minTaskHeight * Time.minute

        const initialDeadline = Time.toTimestamp(state.initialEndDateOnDrag)

        const hoveredDeadline =
          initialDeadline +
          roundNumberTo(draggableState.yTraveled, 15) * Time.minute

        const days = Math.round(
          draggableState.xTraveled / draggableState.parentWidth
        )

        const result = days * Time.day + hoveredDeadline

        if (result <= initialStart) {
          return initialStart
        } else {
          return result
        }
      }

      const initial = (e: React.MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()

        const resizable = ref.current as HTMLDivElement
        if (resizable) {
          const parentWidth = resizable.parentElement.offsetWidth

          draggableStore.setInitial(e.clientX, e.clientY)
          draggableStore.setTraveled(0, 0)
          draggableStore.setParentWidth(parentWidth)
          draggableStore.setDraggedItem({
            id: task.task.id,
            changeAction: 'change-deadline',
            taskType: type,
            height: null,
            width: null,
            top: null,
            backgroundColor: null,
            gtdStage: task.task.gtdStage,
            title: task.task.title,
            isColorDark: task.isColorDark,
            assigned: task.task.assigned,
            isSubtask: task.isSubtask
          })

          if (dragWithoutStartDate) {
            draggableStore.setStartDate(task.roundedDeadline)
            draggableStore.setDeadline(task.roundedDeadline)
          } else {
            draggableStore.setStartDate(task.roundedStartDate)
            draggableStore.setDeadline(task.roundedDeadline)
          }

          store.setShowAllDayWithStartDateTask(true)

          if (dragWithoutStartDate) {
            store.setInitialDragEndDate(task.roundedDeadline)
          } else {
            store.setInitialDragStartDate(task.roundedStartDate)
            store.setInitialDragEndDate(task.roundedDeadline)
          }

          document.addEventListener('mouseup', onMouseUp)
          document.addEventListener('mousemove', onMouseMove)
        }
      }

      const onMouseMove = () => {
        draggableStore.setDeadline(Time.toLocalDate(getDeadlineTime()))
      }

      const onMouseUp = () => {
        const deadline = getDeadlineTime()
        if (deadline !== task.task.deadline) {
          const box = Sync.getCurrentSandbox()
          const taskHub = box.get(task.task.id) as Hub

          if (dragWithoutStartDate) {
            Sticker.getDeadline(taskHub).current.startDate =
              Sticker.getDeadline(taskHub).current.deadline
            Sticker.getDeadline(taskHub).current.deadline =
              deadline + 15 * Time.minute
            taskHub.commit()
            store.syncState(() => draggableStore.clearDraggableState())
          } else {
            Sticker.getDeadline(taskHub).current.startDate = Time.toTimestamp(
              draggableState.startDate
            )
            Sticker.getDeadline(taskHub).current.deadline = deadline
            taskHub.commit()
            store.syncState(() => draggableStore.clearDraggableState())
          }
        } else {
          draggableStore.clearDraggableState()
        }

        document.removeEventListener('mouseup', onMouseUp)
        document.removeEventListener('mousemove', onMouseMove)
      }

      return (
        <>
          {isTaskInPast && (
            <DraggableContainerTw
              $changeDeadlineExpansion={changeDeadlineExpansion}
              $whiteBg
              $taskInPast
            />
          )}
          <DraggableContainerTw
            style={{
              backgroundColor:
                changeDeadlineExpansion && `${task.columnColor}e6`
            }}
            onMouseDown={initial}
            onClick={noPropagate}
            $changeDeadlineExpansion={changeDeadlineExpansion}
            $taskInPast={isTaskInPast}
          >
            {rightPanelStore.state.currentChat?.id === task.task.id &&
              changeDeadlineExpansion && <ActiveTaskBar />}
          </DraggableContainerTw>
          <DraggableTw
            $changeDeadlineExpansion={changeDeadlineExpansion}
            $taskInPast={isTaskInPast}
            onMouseDown={initial}
            onClick={noPropagate}
          />
        </>
      )
    }
  )
)
